import Checkmark20 from '@carbon/icons-react/es/checkmark/20';
import ChevronDown16 from '@carbon/icons-react/es/chevron--down/16';
import Link20 from '@carbon/icons-react/es/link/20';
import Unlink20 from '@carbon/icons-react/es/unlink/20';
import { Dropdown } from 'antd';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { FileAttachment } from 'api/attachments';
import { Button } from 'components/atoms/button';
import { Menu } from 'components/atoms/dropdown';

export const Trigger = styled(Button).attrs({
  size: 'small',
  type: 'text',
})`
  svg {
    margin-right: var(--spacing-04) * -1;
    margin-left: var(--spacing-03);
    color: #000000;
  }
`;

const FooterWrapper = styled.div<{ isOverlay: boolean }>`
  width: 100%;
  height: var(--spacing-09);
  background-color: var(--coolest-gray-20);
  display: flex;
  border-top: ${({ isOverlay }) =>
    isOverlay ? '1px solid var(--gray-20)' : ''};
`;

const DividerBar = styled.div`
  height: 100%;
  width: 1px;
  position: relative;
`;

const Bar = styled.div`
  height: 100%;
  width: 1px;
  border-left: 1px solid var(--gray-20);
`;

const DropdownWrapper = styled.div`
  width: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const LinkButtonWrapper = styled.div<{ isAttachmentLinked: boolean }>`
  position: absolute;
  background-color: ${({ isAttachmentLinked }) =>
    isAttachmentLinked ? 'var(--coolest-gray-40)' : 'var(--coolest-gray-20)'};
  height: var(--spacing-07);
  width: var(--spacing-07);
  display: flex;
  justify-content: center;
  align-items: center;
  top: var(--spacing-03);
  translate: -50%;
  cursor: pointer;
`;

const MenuItem = styled(Menu.Item)`
  display: flex;
  justify-content: space-between;
`;

const renderOverlay = (
  fileList: FileAttachment[],
  setAttachment: (attach: FileAttachment) => void,
  leftAttachment: FileAttachment,
  rightAttachment: FileAttachment,
) => {
  return (
    <Menu>
      <Menu.Header>
        <FormattedMessage id="image_preview.compare.with.version" />
      </Menu.Header>
      {fileList.map((version, index) => (
        <>
          {version.isDeleted === 'false' &&
            rightAttachment.attachmentId !== version.attachmentId && (
              <MenuItem
                key={version.attachmentId}
                title={
                  <FormattedMessage
                    id="image_preview.version.no"
                    values={{ number: fileList.length - index }}
                  />
                }
                onClick={() => {
                  setAttachment(version);
                }}
              >
                <div>
                  <FormattedMessage
                    id="image_preview.version.no"
                    values={{ number: fileList.length - index }}
                  />
                  {version.latestVersion ? (
                    <span>
                      &nbsp;(
                      <FormattedMessage id="image_preview.current" />)
                    </span>
                  ) : null}
                </div>
                {leftAttachment.attachmentId === version.attachmentId && (
                  <Checkmark20 />
                )}
              </MenuItem>
            )}
        </>
      ))}
    </Menu>
  );
};

interface Props {
  isOverlay: boolean;
  attachmentList?: FileAttachment[];
  leftAttachment?: FileAttachment;
  setLeftAttachment: (attach: FileAttachment) => void;
  rightAttachment?: FileAttachment;
  setRightAttachment: (attach: FileAttachment) => void;
  isAttachmentLinked: boolean;
  toggleAttachmentLink: () => void;
}

export const ComparisonFooter: FC<Props> = ({
  isOverlay,
  attachmentList,
  leftAttachment,
  setLeftAttachment,
  rightAttachment,
  setRightAttachment,
  isAttachmentLinked,
  toggleAttachmentLink,
}) => {
  function getSelectedVersion(
    attachmentsList: FileAttachment[],
    attachment: FileAttachment,
  ) {
    return attachmentsList
      .map((attach, index) => {
        if (attach.attachmentId === attachment.attachmentId) {
          return attachmentsList.length - index;
        }

        return null;
      })
      .filter((value) => value !== null)[0];
  }

  return (
    <FooterWrapper isOverlay={isOverlay}>
      <DropdownWrapper>
        {attachmentList && leftAttachment && rightAttachment && (
          <Dropdown
            trigger={['click']}
            overlay={renderOverlay(
              attachmentList,
              setLeftAttachment,
              leftAttachment,
              rightAttachment,
            )}
          >
            <Trigger className="date-criteria">
              <span>
                <FormattedMessage
                  id="image_preview.version.no"
                  values={{
                    number: getSelectedVersion(attachmentList, leftAttachment),
                  }}
                />
                {leftAttachment.latestVersion ? (
                  <span>
                    &nbsp;(
                    <FormattedMessage id="image_preview.current" />)
                  </span>
                ) : null}
              </span>
              <ChevronDown16 />
            </Trigger>
          </Dropdown>
        )}
      </DropdownWrapper>

      <DividerBar>
        <Bar />
        {!isOverlay && (
          <LinkButtonWrapper
            isAttachmentLinked={isAttachmentLinked}
            onClick={toggleAttachmentLink}
          >
            {isAttachmentLinked ? <Link20 /> : <Unlink20 />}
          </LinkButtonWrapper>
        )}
      </DividerBar>

      <DropdownWrapper>
        {attachmentList && rightAttachment && leftAttachment && (
          <Dropdown
            trigger={['click']}
            overlay={renderOverlay(
              attachmentList,
              setRightAttachment,
              rightAttachment,
              leftAttachment,
            )}
          >
            <Trigger className="date-criteria">
              <span>
                <FormattedMessage
                  id="image_preview.version.no"
                  values={{
                    number: getSelectedVersion(attachmentList, rightAttachment),
                  }}
                />
                {rightAttachment.latestVersion ? (
                  <span>
                    &nbsp;(
                    <FormattedMessage id="image_preview.current" />)
                  </span>
                ) : null}
              </span>
              <ChevronDown16 />
            </Trigger>
          </Dropdown>
        )}
      </DropdownWrapper>
    </FooterWrapper>
  );
};
